/**
 * Translation helper functions for conditional and templated translations
 * These functions centralize complex translation logic and ensure proper key usage
 */

type TranslationFunction = (key: string, params?: Record<string, string | number>) => string;

/**
 * Get password validation error message based on remaining attempts
 */
export function getPasswordInvalidMessage(
  t: TranslationFunction,
  options: { remainingAttempts?: number }
): string {
  if (options.remainingAttempts !== undefined && options.remainingAttempts > 0) {
    return t("events.password.invalidWithAttempts", {
      count: options.remainingAttempts,
    });
  }
  return t("events.password.invalidWithoutAttempts");
}

/**
 * Get password placeholder text based on password state
 */
export function getPasswordPlaceholder(
  t: TranslationFunction,
  options: { passwordSet: boolean; passwordInput: string }
): string {
  if (options.passwordSet && !options.passwordInput) {
    return "••••••••";
  }
  if (options.passwordSet) {
    return t("events.form.passwordSetPlaceholder");
  }
  return t("events.form.passwordPlaceholder");
}

/**
 * Get password button text based on loading and locked state
 */
export function getPasswordButtonText(
  t: TranslationFunction,
  options: { loading: boolean; locked: boolean }
): string {
  if (options.loading) {
    return t("common.loading");
  }
  if (options.locked) {
    return t("events.password.locked");
  }
  return t("events.password.submit");
}

/**
 * Get save button text based on saving state
 */
export function getSaveButtonText(
  t: TranslationFunction,
  options: { saving: boolean }
): string {
  if (options.saving) {
    return t("common.loading");
  }
  return t("common.saveChanges");
}

/**
 * Get payment note text based on Stripe enabled status
 */
export function getPaymentNote(
  t: TranslationFunction,
  options: { stripeEnabled: boolean }
): string {
  if (options.stripeEnabled) {
    return t("gifts.payment.stripeNote");
  }
  return t("gifts.payment.defaultNote");
}

